import { Resize } from "@lib/subs";
import { onDestroy } from "@/modules/_";
import { isWebflowEditor } from "@/webflow/detect-editor";

/**
 * Split block "Read more": desktop (≥992px) opens Osmo-style modal; mobile expands `.cc-extended`.
 *
 * Expects **per `.feature-split`**:
 * - `.feature-split-modal` (or any `[data-modal-group-status]`) inside the same split
 * - `[data-modal-target]` on `.u-link-cover` matching a `[data-modal-name]` in that group
 * - Body for injected copy: `[data-split-modal-body]` **or** `.modal__content` inside the active card
 *
 * `data-module="split-block-read-more"` on `<section>` (or ancestor of all `.feature-split`).
 *
 * **Mobile:** `.btn-text` + `.u-sr-only` toggle: initial label (e.g. Read more) → `data-read-less`
 * (default "Read less") while expanded.
 *
 * Collapse: **Escape**, tap **`.cc-extended`**, or tap **Read less**.
 *
 * **Mobile height:** Expand sets `max-height` on the **next animation frames** so layout
 * (`is-expanded` class) is applied before measuring `scrollHeight`, avoiding a sluggish or
 * two-step open. Prefer a short `ease-out` in CSS (e.g. `0.28s ease-out`) instead of a
 * long `ease` on `max-height`. Users with `prefers-reduced-motion` skip the tween.
 */

const DESKTOP_MIN = 992;
const DEFAULT_READ_LESS = "Read less";

function isDesktop(): boolean {
  return window.matchMedia(`(min-width: ${DESKTOP_MIN}px)`).matches;
}

function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function resolveModalBody(panel: HTMLElement): HTMLElement | null {
  return (
    panel.querySelector<HTMLElement>("[data-split-modal-body]") ??
    panel.querySelector<HTMLElement>(".modal__content")
  );
}

export default function (container: HTMLElement, _dataset: DOMStringMap) {
  if (isWebflowEditor()) return;

  const splits = container.querySelectorAll<HTMLElement>(".feature-split");
  if (!splits.length) return;

  const closeSplitModal = (split: HTMLElement) => {
    const modalGroup = split.querySelector<HTMLElement>("[data-modal-group-status]");
    const trigger = split.querySelector<HTMLElement>(".button .u-link-cover");
    const targetName = trigger?.getAttribute("data-modal-target") ?? "";
    if (!modalGroup || !targetName) return;
    if (modalGroup.getAttribute("data-modal-group-status") !== "active") return;

    split.querySelectorAll<HTMLElement>(`[data-modal-target="${targetName}"]`).forEach((el) => {
      el.setAttribute("data-modal-status", "not-active");
    });
    modalGroup.querySelectorAll<HTMLElement>("[data-modal-name]").forEach((m) => {
      m.setAttribute("data-modal-status", "not-active");
    });
    modalGroup.setAttribute("data-modal-group-status", "not-active");
  };

  const closeAllSplitModals = () => {
    splits.forEach((split) => closeSplitModal(split));
  };

  const disposers: Array<() => void> = [];

  const collapseMobileExpanders: Array<() => void> = [];
  const resetMobileLabels: Array<() => void> = [];

  const onKey = (e: KeyboardEvent) => {
    if (e.key !== "Escape") return;
    closeAllSplitModals();
    collapseMobileExpanders.forEach((fn) => fn());
  };
  document.addEventListener("keydown", onKey);
  disposers.push(() => document.removeEventListener("keydown", onKey));

  splits.forEach((split) => {
    const extended = split.querySelector<HTMLElement>(".cc-extended");
    const trigger = split.querySelector<HTMLElement>(".button .u-link-cover");
    if (!extended || !trigger) return;

    const btnText = split.querySelector<HTMLElement>(".button .btn-text");
    const srOnly = trigger.querySelector<HTMLElement>(".u-sr-only");
    const expandLabel = (
      btnText?.textContent?.trim() ||
      srOnly?.textContent?.trim() ||
      "Read more"
    ).trim();
    const collapseLabel = (split.dataset.readLess ?? DEFAULT_READ_LESS).trim();

    const setMobileLabelExpanded = (expanded: boolean) => {
      const label = expanded ? collapseLabel : expandLabel;
      if (btnText) btnText.textContent = label;
      if (srOnly) srOnly.textContent = label;
    };

    resetMobileLabels.push(() => setMobileLabelExpanded(false));

    const modalGroup = split.querySelector<HTMLElement>("[data-modal-group-status]");
    const targetName = trigger.getAttribute("data-modal-target") ?? "modal-a";
    const modalPanel = modalGroup?.querySelector<HTMLElement>(
      `[data-modal-name="${targetName}"]`
    );
    const modalBody = modalPanel ? resolveModalBody(modalPanel) : null;
    const hasDesktopModal = Boolean(modalGroup && modalPanel && modalBody);

    trigger.setAttribute("role", "button");
    trigger.setAttribute("tabindex", "0");
    trigger.setAttribute("href", "#");
    trigger.setAttribute("aria-expanded", "false");
    if (targetName) trigger.setAttribute("data-modal-target", targetName);

    if (modalGroup && hasDesktopModal) {
      modalGroup.querySelectorAll<HTMLElement>("[data-modal-close]").forEach((btn) => {
        const fn = () => closeSplitModal(split);
        btn.addEventListener("click", fn);
        disposers.push(() => btn.removeEventListener("click", fn));
      });
    }

    const openModal = () => {
      if (!modalGroup || !modalBody || !modalPanel) return;
      modalBody.innerHTML = extended.innerHTML;

      split.querySelectorAll<HTMLElement>(`[data-modal-target="${targetName}"]`).forEach((el) => {
        el.setAttribute("data-modal-status", el === trigger ? "active" : "not-active");
      });
      modalGroup.querySelectorAll<HTMLElement>("[data-modal-name]").forEach((m) => {
        m.setAttribute(
          "data-modal-status",
          m.getAttribute("data-modal-name") === targetName ? "active" : "not-active"
        );
      });
      modalGroup.setAttribute("data-modal-group-status", "active");
    };

    const collapseMobile = () => {
      if (!extended.classList.contains("is-expanded")) return;
      if (prefersReducedMotion()) {
        extended.classList.remove("is-expanded");
        extended.style.removeProperty("max-height");
        trigger.setAttribute("aria-expanded", "false");
        setMobileLabelExpanded(false);
        return;
      }
      const h = extended.scrollHeight;
      extended.style.maxHeight = `${h}px`;
      extended.classList.remove("is-expanded");
      requestAnimationFrame(() => {
        extended.style.maxHeight = "0px";
      });
      trigger.setAttribute("aria-expanded", "false");
      setMobileLabelExpanded(false);
    };

    const expandMobile = () => {
      extended.classList.add("is-expanded");
      trigger.setAttribute("aria-expanded", "true");
      setMobileLabelExpanded(true);

      if (prefersReducedMotion()) {
        extended.style.maxHeight = "none";
        return;
      }

      const applyTargetHeight = () => {
        extended.style.maxHeight = `${extended.scrollHeight}px`;
      };

      requestAnimationFrame(() => {
        requestAnimationFrame(applyTargetHeight);
      });
    };

    collapseMobileExpanders.push(collapseMobile);

    const collapseExtendedTap = (e: MouseEvent) => {
      if (isDesktop() || !extended.classList.contains("is-expanded")) return;
      if ((e.target as HTMLElement).closest("a")) return;
      const sel = window.getSelection();
      if (sel && sel.toString().length > 0) return;
      e.preventDefault();
      collapseMobile();
    };
    extended.addEventListener("click", collapseExtendedTap);
    disposers.push(() => extended.removeEventListener("click", collapseExtendedTap));

    const onClick = (e: MouseEvent) => {
      e.preventDefault();

      if (isDesktop() && hasDesktopModal) {
        openModal();
        return;
      }

      if (extended.classList.contains("is-expanded")) {
        collapseMobile();
      } else {
        expandMobile();
      }
    };

    const onKeyTrigger = (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        trigger.click();
      }
    };

    trigger.addEventListener("click", onClick);
    trigger.addEventListener("keydown", onKeyTrigger);
    disposers.push(() => {
      trigger.removeEventListener("click", onClick);
      trigger.removeEventListener("keydown", onKeyTrigger);
    });
  });

  const offResize = Resize.add(() => {
    splits.forEach((split) => {
      const extended = split.querySelector<HTMLElement>(".cc-extended");
      if (extended?.classList.contains("is-expanded")) {
        extended.style.maxHeight = `${extended.scrollHeight}px`;
      }
    });
    if (isDesktop()) {
      collapseMobileExpanders.forEach((fn) => fn());
      resetMobileLabels.forEach((fn) => fn());
    }

    closeAllSplitModals();
  });
  disposers.push(offResize);

  onDestroy(() => {
    disposers.forEach((d) => d());
    collapseMobileExpanders.forEach((fn) => fn());
    resetMobileLabels.forEach((fn) => fn());
    closeAllSplitModals();
  });
}
